import {BLINK_API_URL} from "../../blink-sdk/Constants";
import {Dispatchable} from "../../stem-core/src/base/Dispatcher";
import {WebsocketSubscriber} from "./ws-client/WebsocketSubscriber";
import {AjaxHandler} from "../../stem-core/src/base/Ajax";
import {jsonRequestPreprocessor} from "../../stem-core/src/base/RequestProcessors";
import {Messages} from "../../blinkpay/Messages";
import {isPlainObject, NOOP_FUNCTION} from "../../stem-core/src/base/Utils";
import {GlobalState} from "../../stem-core/src/state/State";
import {BaseEnum} from "../state/misc/BaseEnum";
import {StemDate} from "../../stem-core/src/time/Date";
import {Money} from "../state/misc/Money";

// This needs to be the very last preprocessor, since other preprocessors
// might add more data to the request.
function avoidCORSPreflightRequestsPreProcessor(options) {
    options.headers.delete("X-Requested-With");
    if (options.headers.get("Content-Type") === "application/json") {
        // This is a hack to mark API calls as "simple HTTP requests" according
        // to the CORS spec: https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests
        // The browser bypasses CORS preflight requests when sending simple HTTP
        // requests, making the latency much better. Our API implementation supports
        // the "text/plain" content type and treats it the same as "application/json".
        options.headers.set("Content-Type", "text/plain");
        options.body = JSON.stringify(options.data);
    }
}

class BlinkApiConnectionAjaxHandler extends AjaxHandler {
    getPreprocessors() {
        return [
            ...super.getPreprocessors(),
            avoidCORSPreflightRequestsPreProcessor,
        ];
    }
}

export class BlinkApiConnection extends Dispatchable {
    wsSubscriber = new WebsocketSubscriber();
    ajaxHandler = new BlinkApiConnectionAjaxHandler(null, null);

    constructor(httpApiServerAddresses) {
        super();
        if (!Array.isArray(httpApiServerAddresses)) {
            httpApiServerAddresses = [httpApiServerAddresses];
        }
        this.httpApiServerAddresses = httpApiServerAddresses;
        this.ajaxHandler.addPreprocessor(jsonRequestPreprocessor);
    }

    initWsConnection(authToken) {
        this.wsSubscriber.setAuthToken(authToken);
    }

    clearWsConnection() {
        this.wsSubscriber.disconnect();
    }

    getAjaxHandler() {
        return this.ajaxHandler;
    }

    // TODO: rename Ws to Websocket
    addWsListener(stream, callback) {
        this.wsSubscriber.addStreamListener(stream, callback);
    }

    addWsSubscribeListener(stream, callback) {
        this.wsSubscriber.addStreamSubscribeListener(stream, callback);
    }

    removeWsListener(stream, callback) {
        this.wsSubscriber.removeStreamListener(stream, callback);
    }

    // TODO: callback = null
    getJSON(url, request, callback, extraOptions={}) {
        return this.fetch(url, request, "GET", callback, "json", extraOptions);
    }

    postJSON(url, request, callback, extraOptions={}) {
        return this.fetch(url, request, "POST", callback, "json", extraOptions);
    }

    get(url, request, extraOptions={}) {
        // Will return a promise, as we don't have a callback
        return this.getJSON(url, request, null, extraOptions);
    }

    post(url, request, extraOptions={}) {
        return this.postJSON(url, request, null, extraOptions);
    }

    getApiAddress(path) {
        const index = Math.floor(Math.random() * this.httpApiServerAddresses.length);
        return this.httpApiServerAddresses[index] + path;
    }

    fetch(url, data, method, callback = NOOP_FUNCTION, dataType = "json", extraOptions = {allowNulls: false}) {
        data = {
            ...data,
        };

        const allowNulls = (extraOptions.allowNulls === true) ? true : new Set(extraOptions.allowNulls);

        for (const [key, value] of Object.entries(data)) {
            if (value == null) {
                // Delete nulls, only if there were not whitelisted
                if (allowNulls !== true && !allowNulls.has(key)) {
                    delete data[key];
                }
            }
            // TODO: separate this part!
            if (value instanceof BaseEnum) {
                data[key] = value.valueOf();
            }
            if (value instanceof Money) {
                data[key] = value.amount;
            }
            if (value instanceof Date) {
                data[key] = new StemDate(value).unix();
            }
            if (isPlainObject(value) && method === "GET") {
                data[key] = JSON.stringify(value);
            }
        }

        const xhrPromise = this.ajaxHandler.fetch(this.getApiAddress(url), {
            dataType,
            method,
            data,
            arraySearchParamSuffix: "",
            ...extraOptions,
        });

        if (!callback) {
            return new Promise((resolve, reject) => {
                return xhrPromise.then(
                    response => {
                        if (response.error) {
                            reject(this.getErrorFromResponse(response, xhrPromise));
                        }
                        resolve(response)
                    },
                    error => {
                        reject(this.getPrettifiedApiRuntimeError(error, xhrPromise))
                    }
                )
            });
        }

        return xhrPromise.then(
            response => {
                callback(response, this.getErrorFromResponse(response, xhrPromise));
            },
            error => {
                callback(null, this.getPrettifiedApiRuntimeError(error, xhrPromise));
            }
        );
    }

    getPrettifiedApiRuntimeError(error, xhrPromise) {
        if (!(error instanceof Error)) {
            return error
        }

        const prettifiedError =  {
            statusCode: xhrPromise.getXHR().status,
            detail: error.message,
            stacktrace: error.stack,
            message: error.message === "Network error" ? Messages.networkError : Messages.unexpectedErrorWithCode(xhrPromise.getXHR().status),
        };
        console.error(prettifiedError);

        return prettifiedError;
    }

    getErrorFromResponse(response, xhrPromise) {
        const statusCode = xhrPromise.getXHR().status;

        // Get the error from the API response
        if (response && response.error) {
            return {
                ...response.error,
                statusCode,
            };
        }
        // The whole response body is an error for HTTP codes >= 400
        if (statusCode >= 400) {
            return {
                ...response,
                statusCode,
            };
        }
        // No error found, return null
        return null;
    }
}

// TODO merge the usage of apiConnection and apiClient
export const apiConnection = new BlinkApiConnection(BLINK_API_URL);

// Add a state postprocessor
apiConnection.getAjaxHandler().addPostprocessor((response, xhrPromise) => {
    const {state, events} = response;

    if (state && !xhrPromise.options.disableStateImport) {
        GlobalState.importState(state);
    }
    if (events && !xhrPromise.options.disableEventsImport) {
        GlobalState.applyEvent(events);
    }
});

export const apiClient = {
    get(url, ...args) {
        if (!url.startsWith("/")) {
            url = "/" + url;
        }
        return apiConnection.get(url, ...args);
    },
    post(url, ...args) {
        if (!url.startsWith("/")) {
            url = "/" + url;
        }
        return apiConnection.post(url, ...args);
    }
}
